import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminHolidaysPage.css";
import { useI18n } from "../providers/I18nProvider";
import PaginationControls from "../components/PaginationControls";
import { apiClient } from "../services/apiClient";
import NoirAlert from "../components/NoirAlert";
import { useConfirm } from "../providers/ConfirmProvider";
import { TabBar, FilterBar, SearchInput, FilterField, EmptyState, countBy } from "../components/ListToolbar";

const MIN_YEAR = 2020;
const MAX_YEAR = 2100;
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const AdminHolidaysPage = () => {
  const { t, lang } = useI18n();
  const confirm = useConfirm();
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [tab, setTab] = useState("all"); // all | upcoming | past
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [yearInput, setYearInput] = useState(() => String(new Date().getFullYear()));
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  const pageSizeKey = "pageSize.adminHolidays";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  const locale = lang === "tr" ? "tr-TR" : "en-US";
  const weekdayOf = (dateStr) => {
    const d = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const tabCounts = useMemo(() => {
    const today = todayKey();
    return countBy(items, {
      all: () => true,
      upcoming: (i) => String(i.date).slice(0, 10) >= today,
      past: (i) => String(i.date).slice(0, 10) < today,
    });
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = todayKey();
    const q = search.trim().toLocaleLowerCase(locale);
    return items.filter((i) => {
      const key = String(i.date).slice(0, 10);
      if (tab === "upcoming" && key < today) return false;
      if (tab === "past" && key >= today) return false;
      if (monthFilter && key.slice(5, 7) !== monthFilter) return false;
      if (q && !String(i.name || "").toLocaleLowerCase(locale).includes(q)) return false;
      return true;
    });
  }, [items, tab, search, monthFilter, locale]);

  useEffect(() => {
    setPage(1);
  }, [tab, search, monthFilter, year]);

  useEffect(() => {
    const totalPages = Math.ceil((filteredItems.length || 0) / pageSize) || 1;
    if (page > totalPages) setPage(totalPages);
  }, [filteredItems.length, page, pageSize]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  // Yıl yazılırken ("2","20","202") geçersiz yıllara istek atılmasın: sadece geçerli aralık uygulanır.
  const commitYear = (raw) => {
    setYearInput(raw);
    const n = Number(raw);
    if (Number.isInteger(n) && n >= MIN_YEAR && n <= MAX_YEAR) setYear(n);
  };
  const shiftYear = (delta) => {
    const next = Math.min(MAX_YEAR, Math.max(MIN_YEAR, year + delta));
    setYear(next);
    setYearInput(String(next));
  };

  const loadSeq = React.useRef(0);
  const load = async () => {
    const seq = ++loadSeq.current; // eski yıl isteğinin cevabı yenisini ezmesin
    setLoading(true);
    try {
      const res = await apiClient.get("/public-holidays", {
        params: { year },
      });
      if (seq !== loadSeq.current) return;
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setItems([]);
      setNotification({
        message: err.response?.data?.msg || t("holidays.loadError"),
        type: "error",
      });
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const resetForm = () => {
    setName("");
    setStartDate("");
    setEndDate("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !startDate) {
      setStatusMsg(t("holidays.required"));
      return;
    }
    const payload = {
      name: name.trim(),
      start_date: startDate,
      end_date: endDate || startDate,
    };
    if (endDate && endDate < startDate) {
      setStatusMsg(t("holidays.rangeInvalid"));
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await apiClient.post("/public-holidays", payload);
      setStatusMsg("");
      setNotification({ message: t("holidays.saved"), type: "success" });
      resetForm();
      // Eklenen tatil başka bir yıla düşüyorsa listede görünsün diye o yıla geç
      const addedYear = Number(startDate.slice(0, 4));
      if (addedYear && addedYear !== year && addedYear >= MIN_YEAR && addedYear <= MAX_YEAR) {
        setYear(addedYear);
        setYearInput(String(addedYear));
      } else {
        load();
      }
    } catch (err) {
      setStatusMsg(err.response?.data?.msg || t("holidays.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const ok = await confirm(t("holidays.deleteConfirm", { name: item.name, date: item.date }));
    if (!ok) return;
    try {
      await apiClient.delete("/public-holidays", {
        data: { date: item.date },
      });
      setNotification({ message: t("common.saved"), type: "success" });
      load();
    } catch (err) {
      setNotification({
        message: err.response?.data?.msg || t("common.genericError"),
        type: "error",
      });
    }
  };

  return (
    <div className="admin-holidays-page">
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      <div className="page-head">
        <div>
          <h2>{t("holidays.title")}</h2>
          <p className="muted">{t("holidays.subtitle")}</p>
        </div>
        <div className="year-select">
          <label htmlFor="holiday-year">{t("holidays.year")}</label>
          <div className="year-stepper">
            <button className="ghost" type="button" onClick={() => shiftYear(-1)} aria-label="-1">
              ‹
            </button>
            <input
              id="holiday-year"
              type="number"
              min={MIN_YEAR}
              max={MAX_YEAR}
              value={yearInput}
              onChange={(e) => commitYear(e.target.value)}
              onBlur={() => setYearInput(String(year))}
            />
            <button className="ghost" type="button" onClick={() => shiftYear(1)} aria-label="+1">
              ›
            </button>
          </div>
        </div>
      </div>

      <div className="card form-card">
        <h3>{t("holidays.addTitle")}</h3>
        <form onSubmit={handleSubmit} className="holiday-form">
          <label>
            {t("holidays.name")}
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("holidays.namePlaceholder")}
            />
          </label>
          <label>
            {t("holidays.startDate")}
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </label>
          <label>
            {t("holidays.endDate")}
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <div className="hint">{t("holidays.rangeHint")}</div>
          <button type="submit" className="primary" disabled={saving}>
            {t("holidays.save")}
          </button>
          {statusMsg && <div className="msg">{statusMsg}</div>}
        </form>
      </div>

      <div className="card list-card">
        <h3>{t("holidays.listTitle")}</h3>
        {loading ? (
          <p className="muted">{t("common.loading")}</p>
        ) : items.length === 0 ? (
          <EmptyState message={t("holidays.empty")} />
        ) : (
          <>
            <TabBar
              value={tab}
              onChange={setTab}
              ariaLabel={t("holidays.listTitle")}
              tabs={[
                { key: "all", label: t("holidaysPage.tabs.all"), count: tabCounts.all },
                { key: "upcoming", label: t("holidaysPage.tabs.upcoming"), count: tabCounts.upcoming },
                { key: "past", label: t("holidaysPage.tabs.past"), count: tabCounts.past },
              ]}
            />
            <FilterBar
              showReset={Boolean(search || monthFilter)}
              onReset={() => {
                setSearch("");
                setMonthFilter("");
              }}
            >
              <SearchInput value={search} onChange={setSearch} placeholder={t("holidaysPage.search")} />
              <FilterField label={t("holidaysPage.month")}>
                <select
                  className="lt-input"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                >
                  <option value="">{t("holidaysPage.allMonths")}</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={String(i + 1).padStart(2, "0")}>
                      {new Date(2024, i, 1).toLocaleDateString(locale, { month: "long" })}
                    </option>
                  ))}
                </select>
              </FilterField>
            </FilterBar>
            {filteredItems.length === 0 ? (
              <EmptyState message={t("admin.leaves.emptyFiltered")} />
            ) : (
              <>
                <PaginationControls
                  totalItems={filteredItems.length}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
                <table className="holiday-table">
                  <thead>
                    <tr>
                      <th>{t("holidays.date")}</th>
                      <th>{t("holidaysPage.weekday")}</th>
                      <th>{t("holidays.name")}</th>
                      <th>{t("admin.leaves.columns.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedItems.map((item) => {
                      const d = weekdayOf(item.date);
                      const isWeekendDay = d && (d.getDay() === 0 || d.getDay() === 6);
                      return (
                        <tr key={`${item.date}-${item.name}`}>
                          <td className="mono">{item.date}</td>
                          <td>
                            {d ? d.toLocaleDateString(locale, { weekday: "long" }) : "—"}
                            {isWeekendDay && (
                              <span className="weekend-chip">{t("holidaysPage.weekend")}</span>
                            )}
                          </td>
                          <td>{item.name}</td>
                          <td>
                            <button className="ghost" type="button" onClick={() => handleDelete(item)}>
                              {t("holidays.delete")}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <PaginationControls
                  totalItems={filteredItems.length}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminHolidaysPage;
